import { Component, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { toLocalDateString } from 'src/app/shared/utils/date-filter.util';
import { ContractWithProviderService } from '../../contract-with-provider.service';
import { ContractWithProviderDoc, Organization, Provider } from 'src/typings';

export interface CreateContractDialogData {
  provider?: Provider | null;
  org?: Organization | null;
  number?: string;
  lockContext?: boolean;
}

const selectedEntity: ValidatorFn = (control: AbstractControl) =>
  control.value && typeof control.value === 'object' && Number.isInteger(control.value.id) && control.value.id > 0
    ? null : { selectionRequired: true };
const wholeDays: ValidatorFn = (control: AbstractControl) =>
  Number.isInteger(control.value) ? null : { wholeDays: true };
const contractDates: ValidatorFn = (form: AbstractControl) => {
  const start = toLocalDateString(form.get('start_date')?.value);
  const end = toLocalDateString(form.get('end_date')?.value);
  return start && end && end < start ? { dateOrder: true } : null;
};

@Component({
  selector: 'app-contract-with-provider-create-dialog',
  templateUrl: './create-dialog.component.html',
  styleUrls: ['./create-dialog.component.scss']
})
export class ContractWithProviderCreateDialogComponent {
  saving = false;
  error = '';
  form: FormGroup = this.fb.group({
    provider: new FormControl(this.data?.provider || null, selectedEntity),
    org: new FormControl(this.data?.org || null, selectedEntity),
    number: new FormControl(this.data?.number || '', Validators.maxLength(255)),
    start_date: new FormControl(new Date(), Validators.required),
    end_date: new FormControl(null),
    day_limit: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(2147483647), wholeDays]),
    credit_limit: new FormControl(0, [Validators.required, Validators.min(0), Validators.max(9999999999.99)]),
    active: new FormControl(true)
  }, { validators: contractDates });

  constructor(
    private fb: FormBuilder,
    private contractService: ContractWithProviderService,
    private notificationService: NotificationService,
    private dialogRef: MatDialogRef<ContractWithProviderCreateDialogComponent, ContractWithProviderDoc>,
    @Inject(MAT_DIALOG_DATA) public data: CreateContractDialogData
  ) {}

  get providerControl() { return this.form.controls.provider as FormControl; }
  get orgControl() { return this.form.controls.org as FormControl; }

  save() {
    if (this.saving) return;
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    const value = this.form.getRawValue();
    const payload = {
      provider_id: value.provider.id,
      org_id: value.org.id,
      number: value.number.trim(),
      start_date: toLocalDateString(value.start_date)!,
      end_date: toLocalDateString(value.end_date) || null,
      day_limit: value.day_limit,
      credit_limit: value.credit_limit,
      active: value.active
    };
    this.saving = true;
    this.error = '';
    this.dialogRef.disableClose = true;
    this.form.disable({ emitEvent: false });
    this.contractService.create(payload).subscribe({
      next: (doc) => {
        this.notificationService.notice(`Договор ${doc.number || 'без номера'} создан`);
        this.dialogRef.close(doc);
      },
      error: (err) => {
        this.error = typeof err?.error?.message === 'string' ? err.error.message : 'Не удалось создать договор. Попробуйте ещё раз.';
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.form.enable({ emitEvent: false });
      }
    });
  }

  close() {
    if (!this.saving) this.dialogRef.close();
  }
}
